var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IssueKind } from "../../types/Api";
import { isIrregularKind, isSpecialKind } from "../../utils/issueKind";
import { issueInMonographs } from "../../utils/issueAgenda";
import { issueBoxText, issueTooltipLabels } from "../../utils/issueLabel";
import { VOLUME_STATUS, VOLUME_COLORS } from "../../utils/volumeConstants";
import css from "./IssueBox.module.css";
var BOX_SIZE = 44;
var GAP_SIZE = 6;
var LinkedVolumeArrow = function (_a) {
    var _b;
    var direction = _a.direction, issue = _a.issue, onLinkedVolumeClick = _a.onLinkedVolumeClick;
    var formatMessage = useIntl().formatMessage;
    var linkedVolumeId = issue.linkedVolumeId;
    var handleClick = useCallback(function () {
        if (linkedVolumeId)
            onLinkedVolumeClick === null || onLinkedVolumeClick === void 0 ? void 0 : onLinkedVolumeClick(linkedVolumeId);
    }, [linkedVolumeId, onLinkedVolumeClick]);
    var title = formatMessage({
        id: direction === "previous"
            ? "ui-br-legaldeposits.periodicals.issue.continuedFromVolume"
            : "ui-br-legaldeposits.periodicals.issue.continuesIntoVolume",
    }, { volume: (_b = issue.linkedVolumeLabel) !== null && _b !== void 0 ? _b : "" });
    var content = (React.createElement(React.Fragment, null,
        React.createElement("span", { className: css.spillArrowGlyph, "aria-hidden": "true" }, direction === "previous" ? "⟵" : "⟶"),
        issue.linkedVolumeLabel));
    if (!linkedVolumeId || !onLinkedVolumeClick) {
        return (React.createElement("span", { className: css.spillArrow, title: title }, content));
    }
    return (React.createElement("button", { type: "button", className: "".concat(css.spillArrow, " ").concat(css.spillArrowButton), title: title, "aria-label": title, onClick: handleClick }, content));
};
var getBoxStyle = function (issue) {
    if (issue.continuedFromPreviousVolume)
        return { className: css.stub };
    if (issue.notPublished)
        return { className: css.notPublished };
    if (issue.doubleIssueNote)
        return { className: css.doubleIssue };
    if (isSpecialKind(issue.kind))
        return { className: css.special };
    if (!issue.libraryStatus || issue.libraryStatus === "CREATED") {
        return { className: css.expected };
    }
    var bucket = VOLUME_STATUS[issue.libraryStatus];
    var colors = VOLUME_COLORS[bucket];
    return {
        className: "",
        style: {
            background: colors.bg,
            color: colors.color,
            borderColor: colors.border,
        },
    };
};
var getSpillEdgeColor = function (issue) {
    if (!issue.libraryStatus || issue.libraryStatus === "CREATED") {
        return "#ccc";
    }
    return VOLUME_COLORS[VOLUME_STATUS[issue.libraryStatus]].border;
};
var getStatusIndicator = function (issue, hasSpecialHeader) {
    if (issue.notPublished)
        return "\uD83D\uDEAB";
    if (issue.doubleIssueNote)
        return "\u2795";
    if (isSpecialKind(issue.kind) && !hasSpecialHeader)
        return "\u2B50";
    return "";
};
export var IssueBox = function (_a) {
    var _b, _c;
    var issue = _a.issue, onClick = _a.onClick, onLinkedVolumeClick = _a.onLinkedVolumeClick, dateLabel = _a.dateLabel, overrideWidth = _a.overrideWidth;
    var formatMessage = useIntl().formatMessage;
    var handleClick = useCallback(function () {
        onClick(issue);
    }, [issue, onClick]);
    var mergedCount = (_c = (_b = issue.mergedIssueNumbers) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 1;
    var isMultiIssue = mergedCount > 1;
    var copyHalves = useMemo(function () {
        if (!issue.copyStatuses)
            return [];
        return Object.keys(issue.copyStatuses)
            .map(Number)
            .sort(function (a, b) { return a - b; })
            .map(function (copyNumber) {
            var status = issue.copyStatuses[copyNumber];
            return { copyNumber: copyNumber, bg: VOLUME_COLORS[VOLUME_STATUS[status]].bg };
        });
    }, [issue.copyStatuses]);
    var isMultiCopy = useMemo(function () {
        var _a;
        return ((_a = issue.copyCount) !== null && _a !== void 0 ? _a : 1) > 1 &&
            copyHalves.length > 1 &&
            !issue.notPublished &&
            !issue.doubleIssueNote &&
            !issue.continuedFromPreviousVolume &&
            !isSpecialKind(issue.kind);
    }, [
        issue.copyCount,
        copyHalves.length,
        issue.notPublished,
        issue.doubleIssueNote,
        issue.continuedFromPreviousVolume,
        issue.kind,
    ]);
    var multiIssueStyle = useMemo(function () {
        if (overrideWidth !== undefined)
            return { width: "".concat(overrideWidth, "px") };
        if (isMultiIssue) {
            return {
                width: "".concat(BOX_SIZE * mergedCount + GAP_SIZE * (mergedCount - 1), "px"),
            };
        }
        return undefined;
    }, [isMultiIssue, mergedCount, overrideWidth]);
    var isSpecial = isSpecialKind(issue.kind);
    var isIrregular = isIrregularKind(issue.kind);
    var isStandaloneSpecial = issue.kind === IssueKind.SPECIAL_STANDALONE;
    var inMonographs = issueInMonographs(issue);
    var hasSpecialLayout = isSpecial && !issue.continuedFromPreviousVolume;
    var boxText = issueBoxText(issue);
    var boxStyle = useMemo(function () { return getBoxStyle(issue); }, [issue]);
    var combinedStyle = useMemo(function () { return (__assign(__assign({}, (isMultiCopy ? undefined : boxStyle.style)), multiIssueStyle)); }, [isMultiCopy, boxStyle.style, multiIssueStyle]);
    var className = useMemo(function () {
        return [
            css.box,
            !isMultiCopy && boxStyle.className,
            hasSpecialLayout && css.specialLayout,
            hasSpecialLayout && dateLabel && css.specialLayoutWithDate,
            boxText.name && css.withName,
            issue.parentIssueId && css.childSpecial,
            isIrregular && css.irregular,
            isStandaloneSpecial && css.specialStandalone,
            inMonographs && css.inMonographs,
            issue.continuesIntoNextVolume && css.spillRight,
        ]
            .filter(Boolean)
            .join(" ");
    }, [
        isMultiCopy,
        boxStyle.className,
        hasSpecialLayout,
        dateLabel,
        boxText.name,
        issue.parentIssueId,
        isIrregular,
        isStandaloneSpecial,
        inMonographs,
        issue.continuesIntoNextVolume,
    ]);
    var indicator = getStatusIndicator(issue, hasSpecialLayout);
    var title = useMemo(function () {
        var parts = issueTooltipLabels(issue);
        if (issue.parentIssueId && issue.parentLabel) {
            parts.push(formatMessage({ id: "ui-br-legaldeposits.periodicals.child-special.tooltip" }, { parent: issue.parentLabel }));
        }
        if (issue.notPublished)
            parts.push(formatMessage({
                id: "ui-br-legaldeposits.periodicals.issue.not-published",
            }));
        if (issue.doubleIssueNote)
            parts.push(issue.doubleIssueNote);
        if (isSpecial && issue.specialNote)
            parts.push(issue.specialNote);
        if (inMonographs) {
            parts.push(formatMessage({
                id: "ui-br-legaldeposits.periodicals.issue.inMonographs.".concat(inMonographs),
            }));
        }
        if (isMultiCopy) {
            copyHalves.forEach(function (half) {
                var _a;
                var status = (_a = issue.copyStatuses) === null || _a === void 0 ? void 0 : _a[half.copyNumber];
                if (status) {
                    parts.push("PV".concat(half.copyNumber, ": ").concat(formatMessage({
                        id: "ui-br-legaldeposits.periodicals.issue.status.".concat(status),
                        defaultMessage: status,
                    })));
                }
            });
        }
        else if (issue.libraryStatus) {
            parts.push(formatMessage({
                id: "ui-br-legaldeposits.periodicals.issue.status.".concat(issue.libraryStatus),
                defaultMessage: issue.libraryStatus,
            }));
        }
        return parts.join(" — ");
    }, [issue, isSpecial, inMonographs, isMultiCopy, copyHalves, formatMessage]);
    var legacyDoubleIssueBadge = issue.doubleIssueNote
        ? formatMessage({
            id: "ui-br-legaldeposits.periodicals.legacy-double-issue",
        })
        : null;
    return (React.createElement(React.Fragment, null,
        issue.continuedFromPreviousVolume && (React.createElement(LinkedVolumeArrow, { direction: "previous", issue: issue, onLinkedVolumeClick: onLinkedVolumeClick })),
        React.createElement("button", { type: "button", className: className, style: combinedStyle, onClick: handleClick, title: title },
            isMultiCopy && (React.createElement("span", { className: css.splitBg, "aria-hidden": "true" }, copyHalves.map(function (half) { return (React.createElement("span", { key: half.copyNumber, className: css.splitHalf, style: { background: half.bg } })); }))),
            isIrregular && (React.createElement("span", { className: css.kindBadge },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.issue.badge.irregular" }))),
            inMonographs && (React.createElement("span", { className: css.agendaBadge, "data-test-id": "issue-in-monographs", "data-in-monographs": inMonographs },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.issue.badge.inMonographs" }))),
            isStandaloneSpecial && !hasSpecialLayout && (React.createElement("span", { className: css.kindBadge },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.issue.badge.special-standalone" }))),
            hasSpecialLayout ? (React.createElement(React.Fragment, null,
                React.createElement("span", { className: css.specialHead },
                    React.createElement("span", { "aria-hidden": "true" }, "\u2605"),
                    indicator && React.createElement("span", { "aria-hidden": "true" }, indicator),
                    isStandaloneSpecial && (React.createElement("span", { className: css.kindBadgeInline },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.periodicals.issue.badge.special-standalone" })))),
                React.createElement("span", { className: "".concat(css.label, " ").concat(css.clamp, " ").concat(css.specialLabel) }, boxText.main))) : (React.createElement("span", { className: css.label }, boxText.main)),
            boxText.name && (React.createElement("span", { className: css.issueName, "data-testid": "issue-name" }, boxText.name)),
            dateLabel && (React.createElement("span", { className: css.dateLabel, "data-testid": "issue-date-label" }, dateLabel)),
            indicator && !hasSpecialLayout && (React.createElement("span", { className: css.indicator }, indicator)),
            legacyDoubleIssueBadge && (React.createElement("span", { className: css.legacyDoubleIssueBadge, title: issue.doubleIssueNote }, legacyDoubleIssueBadge)),
            issue.continuedFromPreviousVolume && (React.createElement("span", { className: "".concat(css.spillEdge, " ").concat(css.spillEdgeLeft), "aria-hidden": "true" })),
            issue.continuesIntoNextVolume && (React.createElement("span", { className: "".concat(css.spillEdge, " ").concat(css.spillEdgeRight), style: { backgroundColor: getSpillEdgeColor(issue) }, "aria-hidden": "true" }))),
        issue.continuesIntoNextVolume && (React.createElement(LinkedVolumeArrow, { direction: "next", issue: issue, onLinkedVolumeClick: onLinkedVolumeClick }))));
};
